<template>
  <div class="pa-4" style="position: relative; min-height: 60vh">
    <v-overlay
      :value="loading"
      absolute
      color="white"
      opacity="0.75"
      z-index="6"
    >
      <v-progress-circular indeterminate color="primary" size="64" width="5" />
    </v-overlay>

    <breadcrumb />

    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <h2 class="black--text mb-0">{{ $t("reports.aiClient.manageTitle") }}</h2>
      <div class="d-flex" style="gap: 10px">
        <v-btn color="primary" depressed @click="goUsageLog">{{
          $t("reports.aiClient.usageLogBtn")
        }}</v-btn>
        <v-btn color="primary" depressed @click="goBack">{{
          $t("reports.aiClient.back")
        }}</v-btn>
      </div>
    </div>

    <v-card outlined class="pa-5">
      <!-- Summary cards -->
      <v-row class="mb-2">
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 cs-avail">
            <div class="black--text font-weight-bold mb-1">
              {{ $t("reports.aiClient.cardAvailable") }}
            </div>
            <div class="d-flex align-baseline flex-wrap" style="gap: 8px">
              <div class="text-h4 font-weight-bold black--text">
                {{ formatNum(summary.available) }}
              </div>
              <span class="text-body-2" v-if="summary.refreshesOn">
                {{ $t("reports.aiClient.refreshesOn") }}:
                {{ summary.refreshesOn | formatDate }}
              </span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4">
            <div class="black--text font-weight-bold mb-1">
              {{ $t("reports.aiClient.cardTotal") }}
            </div>
            <div class="d-flex align-baseline flex-wrap" style="gap: 8px">
              <div class="text-h4 font-weight-bold black--text">
                {{ formatNum(summary.total) }}
              </div>
              <span class="text-body-2" v-if="summary.assignedOn">
                {{ $t("reports.aiClient.assignedOn") }}:
                {{ summary.assignedOn | formatDate }}
              </span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 cs-used">
            <div class="black--text font-weight-bold mb-1">
              {{ $t("reports.aiClient.cardUsed") }}
            </div>
            <div class="text-h4 font-weight-bold black--text">
              {{ formatNum(summary.used) }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <h3 class="black--text">{{ $t("reports.aiClient.distBudgetTitle") }}</h3>
      <p class="mb-4">{{ $t("reports.aiClient.distBudgetHelp") }}</p>

      <!-- Group config cards -->
      <v-card
        v-for="g in groups"
        :key="g.ownerType"
        outlined
        class="pa-5 mb-4 group-card"
      >
        <h3 class="green--text text--darken-1 mb-3">
          {{ groupLabel(g.ownerType) }}
        </h3>

        <div class="mb-1 text-body-2">
          {{ $t("reports.aiClient.monthlyCredits") }}
        </div>
        <div class="credit-control mb-4">
          <input
            v-model.number="g.monthly"
            type="number"
            min="0"
            class="credit-input"
          />
          <span class="credit-suffix">{{
            $t("reports.aiClient.credits")
          }}</span>
        </div>

        <v-divider class="mb-4" />

        <div class="black--text font-weight-bold mb-3">
          {{ $t("reports.aiClient.aiReports") }}
        </div>
        <div class="toggles-row">
          <div v-for="rt in reportTypes" :key="rt" class="toggle-item">
            <div class="text-body-2 mb-2">{{ reportLabel(rt) }}</div>
            <div class="d-flex align-center">
              <span
                class="text-body-2 mr-2"
                :class="g.access[rt] ? 'green--text text--darken-1' : ''"
              >
                {{
                  g.access[rt]
                    ? $t("reports.aiClient.enabled")
                    : $t("reports.aiClient.disabled")
                }}
              </span>
              <v-switch
                v-model="g.access[rt]"
                inset
                color="green"
                class="mt-0 pt-0"
                hide-details
                dense
              />
            </div>
          </div>
        </div>
      </v-card>

      <!-- Actions -->
      <div class="d-flex justify-end mt-4" style="gap: 10px">
        <v-btn outlined color="primary" @click="goBack">{{
          $t("reports.aiClient.cancel")
        }}</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          :disabled="loading"
          @click="save"
        >
          {{ $t("reports.aiClient.save") }}
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";

// Client-facing report types shown in the Manage screen (Figma: 5 types).
const REPORT_TYPES = [
  "vegetation_vigor",
  "farm_productivity",
  "anomaly_detection",
  "yield_prediction",
  "disease_detection",
];
const GROUP_ORDER = ["cooperative", "buying_station", "farmer"];

export default {
  name: "ManageAiReporting",
  mixins: [creditFormat],
  data() {
    return {
      summary: { available: 0, total: 0, used: 0 },
      groups: [],
      reportTypes: REPORT_TYPES,
      loading: false,
      saving: false,
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const env = await AiCreditService.getClientConfig();
        const payload = (env && env.data) || {};
        this.summary = payload.pool || this.summary;
        const accessMap = payload.access || {};
        const byType = {};
        (payload.groups || []).forEach((g) => (byType[g.ownerType] = g));
        // Build an editable model in a fixed group order; access defaults to enabled.
        this.groups = GROUP_ORDER.map((ownerType) => {
          const src = byType[ownerType] || { ownerType, monthly: 0 };
          const groupAccess = accessMap[ownerType] || {};
          const access = {};
          REPORT_TYPES.forEach((rt) => {
            access[rt] = groupAccess[rt] !== false; // absent = enabled
          });
          return { ownerType, monthly: Number(src.monthly) || 0, access };
        });
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiClient.errorTitle"),
          text: this.$t("reports.aiClient.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const payload = {
          groups: this.groups.map((g) => ({
            ownerType: g.ownerType,
            monthly: Math.max(0, Number(g.monthly) || 0),
          })),
          access: this.groups.map((g) => ({
            ownerType: g.ownerType,
            entries: REPORT_TYPES.map((rt) => ({
              reportType: rt,
              enabled: !!g.access[rt],
            })),
          })),
        };
        const data = await AiCreditService.saveClientConfig(payload);
        if (data && data.success === false) {
          const msg =
            data.message || this.$t("reports.aiClient.overAllocation");
          this.$notify({
            title: this.$t("reports.aiClient.errorTitle"),
            text: msg,
            type: "error",
          });
          return;
        }
        this.$notify({
          title: this.$t("reports.aiClient.successTitle"),
          text: this.$t("reports.aiClient.saved"),
          type: "success",
        });
        this.applyConfig(data && data.data);
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiClient.errorTitle"),
          text: this.$t("reports.aiClient.saveFailed"),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    applyConfig(payload) {
      if (payload && payload.pool) this.summary = payload.pool;
    },
    reportLabel(reportType) {
      const key = `reports.aiClient.rt_${reportType}`;
      const label = this.$t(key);
      return label === key ? reportType : label;
    },
    groupLabel(ownerType) {
      return this.$t(`reports.aiClient.group_${ownerType}`);
    },
    goUsageLog() {
      this.$router.push({ name: "aiReportsUsageLog" });
    },
    goBack() {
      this.$router.push({ name: "aiReportsDashboard" });
    },
  },
};
</script>

<style scoped>
.cs-avail {
  background: #f1faf4;
  border-color: #b7e3c9 !important;
}
.cs-used {
  background: #fff4ee;
  border-color: #ffd3bd !important;
}
.group-card {
  border-color: #e6e6e6 !important;
  border-radius: 12px;
}

/* Monthly Credits control — white input + grey "Credits" suffix box */
.credit-control {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  height: 46px;
  max-width: 320px;
  width: 100%;
}
.credit-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0 14px;
  font-size: 15px;
  color: #222;
  background: #fff;
}
.credit-input::-webkit-outer-spin-button,
.credit-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.credit-input {
  -moz-appearance: textfield;
  appearance: textfield;
}
.credit-suffix {
  display: flex;
  align-items: center;
  padding: 0 16px;
  color: #7a7a7a;
  font-size: 14px;
  background: #f0f0f0;
  border-left: 1px solid #d9d9d9;
}

/* 5-across toggle row */
.toggles-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 12px;
}
.toggle-item {
  flex: 1 1 160px;
  min-width: 150px;
}
</style>
